import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export interface Agent {
  id: string;
  name: string;
  displayName: string;
  role: string;
  description: string;
  status: 'active' | 'draft' | 'paused' | 'marketplace';
  avatarUrl: string;
  model: string;
  temperature: number;
  maxTokens: number;
  languages: string[];
  systemPrompt: string;
  tools: string[];
  deployment: string[];
  price: number;
  metrics: {
    runs: number;
    avgCost: number;
    successRate: number;
  };
}
interface AgentsState {
  agents: Agent[];
  viewMode: 'grid' | 'table';
  activeAgentId: string | null;
  addAgent: (agent: Agent) => void;
  updateAgent: (id: string, updates: Partial<Agent>) => void;
  deleteAgent: (id: string) => void;
  cloneAgent: (id: string) => void;
  subscribeToAgent: (agent: Agent) => void;
  toggleViewMode: () => void;
  setActiveAgent: (id: string | null) => void;
}
const SAMPLE_AGENTS: Agent[] = [
  {
    id: 'agent-1',
    name: 'Customer Support Pro',
    displayName: 'Support Hero',
    role: 'Support',
    description: 'Expert in solving technical issues and customer queries.',
    status: 'active',
    avatarUrl: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=256&h=256&fit=crop',
    model: 'GPT-4o',
    temperature: 0.7,
    maxTokens: 2000,
    languages: ['en', 'vi'],
    systemPrompt: 'You are a professional support agent...',
    tools: ['WebSearch', 'Gmail'],
    deployment: ['Widget', 'Slack'],
    price: 0,
    metrics: { runs: 1240, avgCost: 0.15, successRate: 98.2 }
  },
  {
    id: 'agent-2',
    name: 'Sales Optimizer',
    displayName: 'SalesBot',
    role: 'Sales',
    description: 'Specialized in lead generation and conversion strategies.',
    status: 'marketplace',
    avatarUrl: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=256&h=256&fit=crop',
    model: 'Claude 3.5 Sonnet',
    temperature: 1.0,
    maxTokens: 4000,
    languages: ['en'],
    systemPrompt: 'You are a high-performing sales closer...',
    tools: ['WebSearch'],
    deployment: ['API'],
    price: 49,
    metrics: { runs: 850, avgCost: 0.22, successRate: 94.5 }
  },
  {
    id: 'agent-3',
    name: 'Content Strategist',
    displayName: 'Writer AI',
    role: 'Marketing',
    description: 'Creates viral content and SEO optimized articles.',
    status: 'draft',
    avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=256&h=256&fit=crop',
    model: 'GPT-4o',
    temperature: 1.2,
    maxTokens: 8000,
    languages: ['en', 'vi', 'cn'],
    systemPrompt: 'You are a creative content marketer...',
    tools: [],
    deployment: [],
    price: 0,
    metrics: { runs: 0, avgCost: 0, successRate: 0 }
  }
];
export const useAgentsStore = create<AgentsState>()(
  persist(
    (set) => ({
      agents: SAMPLE_AGENTS,
      viewMode: 'grid',
      activeAgentId: null,
      addAgent: (agent) => set((state) => ({ agents: [agent, ...state.agents] })),
      updateAgent: (id, updates) => set((state) => ({
        agents: state.agents.map((a) => a.id === id ? { ...a, ...updates } : a)
      })),
      deleteAgent: (id) => set((state) => ({
        agents: state.agents.filter((a) => a.id !== id)
      })),
      cloneAgent: (id) => set((state) => {
        const target = state.agents.find((a) => a.id === id);
        if (!target) return state;
        const newAgent: Agent = {
          ...target,
          id: `agent-${crypto.randomUUID()}`,
          name: `${target.name} (Copy)`,
          metrics: { runs: 0, avgCost: 0, successRate: 0 },
          status: 'draft'
        };
        return { agents: [newAgent, ...state.agents] };
      }),
      subscribeToAgent: (agent) => set((state) => {
        const newAgent: Agent = {
          ...agent,
          id: `sub-${crypto.randomUUID()}`,
          status: 'active',
          metrics: { runs: 0, avgCost: 0, successRate: 0 }
        };
        return { agents: [newAgent, ...state.agents] };
      }),
      toggleViewMode: () => set((state) => ({ viewMode: state.viewMode === 'grid' ? 'table' : 'grid' })),
      setActiveAgent: (id) => set({ activeAgentId: id }),
    }),
    {
      name: 'cloudface-agents-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
// Primitive selectors
export const useAgents = () => useAgentsStore((s) => s.agents);
export const useAgentsCount = () => useAgentsStore((s) => s.agents.length);
export const useAgentViewMode = () => useAgentsStore((s) => s.viewMode);
export const useActiveAgentId = () => useAgentsStore((s) => s.activeAgentId);
// Action selectors
export const useAddAgent = () => useAgentsStore((s) => s.addAgent);
export const useUpdateAgent = () => useAgentsStore((s) => s.updateAgent);
export const useDeleteAgent = () => useAgentsStore((s) => s.deleteAgent);
export const useCloneAgent = () => useAgentsStore((s) => s.cloneAgent);
export const useSubscribeToAgent = () => useAgentsStore((s) => s.subscribeToAgent);
export const useToggleViewMode = () => useAgentsStore((s) => s.toggleViewMode);
export const useSetActiveAgent = () => useAgentsStore((s) => s.setActiveAgent);